// The autonomy dial's model (Brief 4 / P3 — Govern). Pure + testable: the
// authority ladder (the operator's mental model "how much can it do before it
// needs me"), the engine↔operator-language mapping, and the optimistic dial
// state machine (echo the operator's drag <100ms, reconcile the ENFORCED rule
// from GET /rules — never render a rule in-effect before the server confirms).
//
// The four engine strings are the source of truth; labels are presentation
// (decision: map to the existing `operator_rule.authority` axis, never invent
// stops, never show the raw enum in the primary UI). The dial governs the GUARD
// operator (supervisor/Supervisor) authority — the one that gates the halt; the
// arbiters are always advisory and ignore it.

import type { ContactFrequency, RuleAuthority, RuleScopeKind } from "@waytide/core";

/** The authority ladder, LOW → HIGH. Up = more autonomy = fewer interrupts. */
export const AUTHORITY_LADDER: RuleAuthority[] = [
  "read_only",
  "pause_on_destructive",
  "pause_on_commit",
  "full",
];

/** The operator-language stop label (never the raw enum in the primary UI). */
export function authorityLabel(a: RuleAuthority): string {
  switch (a) {
    case "read_only":
      return "Watch only";
    case "pause_on_destructive":
      return "Stop before anything irreversible";
    case "pause_on_commit":
      return "Stop before it commits";
    case "full":
      return "Run on its own";
  }
}

/** The one-line meaning of a stop, operator language. */
export function authorityMeaning(a: RuleAuthority): string {
  switch (a) {
    case "read_only":
      return "It looks and reports; it changes nothing without me.";
    case "pause_on_destructive":
      return "It works; it halts at destructive or irreversible moves.";
    case "pause_on_commit":
      return "It works and proposes; it halts before it writes or commits.";
    case "full":
      return "Full autonomy; it acts and I watch the evidence.";
  }
}

/** The dial position (0..3) of `a` on the ladder. */
export function authorityIndex(a: RuleAuthority): number {
  return AUTHORITY_LADDER.indexOf(a);
}

/** A sensible contact-frequency default per stop (a v1 guess, to be tuned by
 * the interrupt-rate telemetry). More autonomy → quieter by default. */
export function defaultContactFrequency(a: RuleAuthority): ContactFrequency {
  switch (a) {
    case "full":
      return "low";
    case "read_only":
    case "pause_on_destructive":
    case "pause_on_commit":
      return "normal";
  }
}

/** Moving to `to` LOWERS protection iff it grants more autonomy (a higher ladder
 * position) than `from`. Dropping authority on a protective scope is the one
 * dial move that earns a calm inline confirm — never a rubber-stamp prompt. */
export function isLoweringProtection(
  from: RuleAuthority,
  to: RuleAuthority,
): boolean {
  return authorityIndex(to) > authorityIndex(from);
}

// ── Dial scope (Brief 4 / P3 — "set policy once, not per session") ──

/** Where a dial governs: ONE session, a whole PROJECT (every session in it that
 * has no session rule of its own), or the STANDING default — every session that
 * has no more-specific rule. Resolution is session ▸ project ▸ global (UC-6 /
 * Brief 4 P8). */
export type DialScope =
  | { kind: "session"; threadId: number }
  | { kind: "project"; projectKey: string; projectName?: string }
  | { kind: "global" };

/** The `operator_rule` scope fields for a dial scope. A global rule MUST carry
 * an EMPTY scope_key (the backend couples "scope_key empty iff global"); a
 * session rule keys on its thread id; a project rule keys on its project id.
 * Pure. */
export function ruleScopeFields(scope: DialScope): {
  scope_kind: RuleScopeKind;
  scope_key: string;
} {
  switch (scope.kind) {
    case "session":
      return { scope_kind: "session", scope_key: String(scope.threadId) };
    case "project":
      return { scope_kind: "project", scope_key: scope.projectKey };
    case "global":
      return { scope_kind: "global", scope_key: "" };
  }
}

/** The calm scope label under the dial caption. */
export function scopeLabel(scope: DialScope): string {
  switch (scope.kind) {
    case "session":
      return "this session";
    case "project":
      return scope.projectName
        ? `${scope.projectName} · project`
        : "this project";
    case "global":
      return "every session · default";
  }
}

// ── The optimistic dial state machine (reuses P2's echo↔confirm pattern) ──

export interface DialState {
  /** The server-confirmed effective authority (from GET /rules). */
  effective: RuleAuthority;
  /** An optimistic change awaiting the POST /rules confirm; null when settled.
   * While set, the dial shows the pending value but it is NOT yet in effect. */
  pending: RuleAuthority | null;
}

export type DialAction =
  | { type: "set"; authority: RuleAuthority } // optimistic echo of the drag
  | { type: "confirm"; authority: RuleAuthority } // the truth from GET /rules
  | { type: "fail" }; // the write failed → roll back to server truth

export function dialReducer(state: DialState, action: DialAction): DialState {
  switch (action.type) {
    case "set":
      return { ...state, pending: action.authority };
    case "confirm":
      // Reconcile to the enforced truth (which may differ from the echo).
      return { effective: action.authority, pending: null };
    case "fail":
      // Never a stuck optimistic state — roll back to the confirmed value.
      return { ...state, pending: null };
  }
}

/** What the dial reads RIGHT NOW: the optimistic value while pending (shown but
 * not yet in effect), else the confirmed effective authority. */
export function dialDisplay(state: DialState): {
  authority: RuleAuthority;
  pending: boolean;
} {
  return state.pending
    ? { authority: state.pending, pending: true }
    : { authority: state.effective, pending: false };
}
